import { message, notification, Select, Alert, Spin } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  ModalItem,
  ButtonItem,
  Form,
  FormItem,
  SelectItem,
  RangePickerItem,
  ErrorItem,
} from "../../../../Components/Items";
import ModalProgreso from "../../../../Components/ModalProgreso";
import SeguidorProcesoMasivo from "../../../../Components/seguirProcesoMasivo";

const { Option } = Select;

/**
 * Verificacion masiva del estatus fiscal de las facturas ante el SAT.
 *
 * El criterio es propio y a proposito NO el filtro de la tabla: el de selectVentas
 * arrastra busqueda de texto, filtros por columna y lookups, y reusarlo aqui haria
 * impredecible cuantas facturas se van a consultar. Aqui es un rango de fechas y un
 * universo, se cuenta antes de lanzar y hay un tope por corrida.
 *
 * **No cancela nada.** Registra lo que contesto el SAT y marca las discrepancias
 * (facturas que el SAT reporta canceladas y en el sistema siguen vivas). Aplicar la
 * reversa —polizas, inventario, cobros, banco— sigue siendo de una en una con la
 * lupa del folio: en lote seria desarmar la contabilidad de decenas de ventas sin
 * que nadie las mire.
 */
class VerificarEstatusFiscalMasivo extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    contando: false,
    conteo: null,
    tope: null,
    procesando: false,
    progreso: { procesados: 0, total: null },
  };

  componentDidMount() {
    // El proceso corre en el servidor por lotes y avisa por WebSocket.
    this.seguidor = new SeguidorProcesoMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      onProgreso: (progreso) => this.setState({ procesando: true, progreso }),
      onFin: (resumen) => {
        this.setState({ procesando: false, progreso: { procesados: 0, total: null } });

        // Sin resumen el proceso termino pero no dejo numeros (murio, o caduco su
        // resultado): se cierra sin anunciar cifras inventadas.
        if (resumen) {
          notification.info({
            message: "Verificacion terminada",
            duration: null,
            description: this.textoResumen(resumen),
          });
        }

        this.cerrar(true);
      },
      onError: (error) => {
        this.setState({ procesando: false, progreso: { procesados: 0, total: null } });
        message.error(error || "Hubo un error y no se pudo terminar la verificacion");
      },
    });
    this.seguidor.escuchar();
  }

  componentWillUnmount() {
    if (this.seguidor) this.seguidor.detener();
  }

  textoResumen(resumen) {
    const partes = [
      `Revisadas: ${resumen.revisadas ?? 0}`,
      `vigentes en el SAT: ${resumen.vigentesEnSat ?? 0}`,
      `canceladas en el SAT: ${resumen.canceladasEnSat ?? 0}`,
    ];

    if (resumen.discrepancias > 0) {
      partes.push(`${resumen.discrepancias} aparecen canceladas en el SAT pero siguen vigentes en el sistema (revisalas con la lupa del folio)`);
    }
    if (resumen.noEncontradas > 0) {
      partes.push(`${resumen.noEncontradas} no las encontro el SAT (revisa RFC y total)`);
    }
    if (resumen.sinRespuesta > 0) {
      partes.push(`${resumen.sinRespuesta} sin respuesta del SAT`);
    }
    if (resumen.omitidas > 0) {
      partes.push(`${resumen.omitidas} quedaron sin consultar porque el SAT dejo de responder`);
    }

    return partes.join(". ") + ".";
  }

  cerrar(recargar) {
    this.setState({ conteo: null, tope: null });
    if (this.formRef.current) this.formRef.current.resetFields();
    this.props.cerrar(recargar);
  }

  // El conteo se pide al cambiar los criterios: asi la pantalla dice cuantas se van a
  // consultar ANTES de arrancar, y puede pedir acortar el rango en vez de lanzar un
  // proceso que el backend va a rechazar solo.
  contar() {
    const valores = this.formRef.current?.getFieldsValue();
    const rango = valores?.rango;

    if (!rango || !rango[0] || !rango[1]) {
      this.setState({ conteo: null, tope: null });
      return;
    }

    this.setState({ contando: true });

    axios
      .post(
        "ventas/contarVerificacionFiscalMasiva",
        {
          empresaId: this.props.empresaId,
          fechaInicio: moment(rango[0]).format("YYYY-MM-DD"),
          fechaFin: moment(rango[1]).format("YYYY-MM-DD"),
          universo: valores.universo || "ambas",
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ contando: false });
        if (res.data.success === 2) {
          this.setState({ conteo: res.data.total, tope: res.data.tope });
        } else {
          this.setState({ conteo: null, tope: null });
        }
      })
      .catch(() => this.setState({ contando: false, conteo: null, tope: null }));
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  onFinish(values) {
    this.setState({ loading: true });

    axios
      .post(
        "ventas/verificarEstatusFiscalMasivo",
        {
          empresaId: this.props.empresaId,
          fechaInicio: moment(values.rango[0]).format("YYYY-MM-DD"),
          fechaFin: moment(values.rango[1]).format("YYYY-MM-DD"),
          universo: values.universo || "ambas",
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });

        if (res.data.success === 2) {
          // Arranco. El avance y el cierre llegan por el seguidor.
          this.seguidor.seguir(res.data.procesoId);
          this.setState({
            procesando: true,
            progreso: { procesados: 0, total: res.data.total ?? null },
          });
        } else if (res.data.success === 1) {
          message.error(res.data.message || "No se pudo iniciar la verificacion");
        } else {
          // Incluye el rechazo por candado: ya hay otra operacion masiva en curso
          // para esta empresa.
          message.error(res.data.message || "Hubo un error y no se inicio la verificacion");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Hubo un error y no se inicio la verificacion");
      });
  }

  render() {
    const { conteo, tope } = this.state;
    const excede = conteo !== null && tope !== null && conteo > tope;

    return (
      <>
        <ModalItem
          title="Verificar estatus fiscal en el SAT"
          visible={this.props.visible}
          spinning={this.state.loading}
          onCancel={() => this.cerrar(false)}
          paddingTop
        >
          <Form
            ref={this.formRef}
            name="verificarEstatusFiscalMasivo"
            layout
            width={700}
            initialValues={{ universo: "ambas" }}
            onFinish={this.onFinish.bind(this)}
            onFinishFailed={this.onFinishFailed}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 15 }}
              message="Solo consulta, no cancela"
              description="Se le pregunta al SAT el estatus real de cada factura del rango y se guarda la respuesta. Las que el SAT reporte canceladas y en el sistema sigan vigentes quedan marcadas en la lista; la cancelacion se aplica una por una desde la lupa del folio."
            />

            <FormItem
              label="Fechas"
              name="rango"
              required
              margin
            >
              <RangePickerItem
                placeholder={["Inicio", "Fin"]}
                onChange={() => this.contar()}
              />
            </FormItem>

            <FormItem label="Facturas" name="universo" margin>
              <SelectItem placeholder="Facturas" onChange={() => this.contar()}>
                <Option value="ambas">Todas las facturas del rango</Option>
                <Option value="canceladas">Solo las canceladas en el sistema</Option>
                <Option value="vigentes">Solo las vigentes en el sistema</Option>
              </SelectItem>
            </FormItem>

            <div style={{ marginBottom: 15, minHeight: 22 }}>
              {this.state.contando ? (
                <Spin size="small" />
              ) : (
                conteo !== null && (
                  <span style={{ color: excede ? "#f5222d" : "#666" }}>
                    {conteo === 0
                      ? "No hay facturas timbradas en ese rango con esos criterios."
                      : excede
                        ? `El rango tiene ${conteo} facturas y el maximo por corrida es ${tope}. Acorta el rango de fechas.`
                        : `Se van a consultar ${conteo} factura${conteo > 1 ? "s" : ""} en el SAT.`}
                  </span>
                )
              )}
            </div>

            <ButtonItem
              type="primary"
              htmlType="submit"
              block
              disabled={!conteo || excede}
            >
              Verificar en el SAT
            </ButtonItem>
          </Form>
        </ModalItem>

        <ModalProgreso
          visible={this.state.procesando}
          titulo="Verificando en el SAT"
          descripcion="No cierres esta ventana: el proceso sigue en el servidor, pero perderias el avance en pantalla. Las facturas se consultan de a pocas y con pausas para no saturar el servicio del SAT, asi que tarda."
          procesados={this.state.progreso.procesados}
          total={this.state.progreso.total}
          unidad="facturas"
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(VerificarEstatusFiscalMasivo);
